import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BrickWall } from 'lucide-react';

import type { ProjectLayoutType } from '@/api/media';
import { getMediaOptions } from '@/api/media';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_FLOORPLANS_INPUT_TYPE,
  isPdfFloorPlan,
  uploadLayoutsSchema,
} from '@/features/estate-media/schema/upload-media';
import { useUploadFloorLayouts } from '@/features/project-media/api/upload-floor-layout';
import { env } from '@/constants/env';
import {
  getLatestItem,
  MEDIA_SECTION_LINK_CLASS,
  MediaSectionCard,
} from '@/features/estate-media/components/media-section-card';
import { useModal } from '../../../../components/modals/new/use-modal';
import { UploadMediaModal } from '../../../estate-media/components/upload-media-modal';

type FloorPlansCard = {
  data: ProjectLayoutType[][];
  projectId: string;
  canWriteMedia: boolean;
};

export const FloorPlansCard = ({ data = [], projectId, canWriteMedia }: FloorPlansCard) => {
  const { t } = useTranslation();
  const modal = useModal();

  const images = data?.flat() || [];
  const amount = images.length;

  const queryClient = useQueryClient();

  const { mutateAsync: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
      },
    },
  });

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadFloorLayouts({ layouts: dataTransfer.files, projectId });
    modal.dismiss();
  };

  const handleOpenModal = () => {
    modal.open();
  };

  // a PDF cannot render as the <img> preview, so pick the newest image instead
  const latest = getLatestItem(images.filter((image) => !isPdfFloorPlan(image)));

  return (
    <>
      <MediaSectionCard
        title={t('floorPlans')}
        icon={<BrickWall className="h-4 w-4" />}
        count={amount}
        previewSrc={latest?.mediaId ? `${env.API_URL}/images/${latest.mediaId}` : undefined}
        link={
          <Link
            to="/projects/$projectId/media/floor-plans"
            params={{ projectId }}
            className={MEDIA_SECTION_LINK_CLASS}
            aria-label={t('floorPlans')}
          />
        }
        onUpload={canWriteMedia ? handleOpenModal : undefined}
      />
      <UploadMediaModal
        modal={modal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_FLOORPLANS_INPUT_TYPE}
        acceptedMediaSchema={uploadLayoutsSchema()}
      />
    </>
  );
};
